/** XeAgents.ai - Revenue Recognition Dashboard (ASC 606 / IFRS 15) */
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import {
  Loader2,
  RefreshCw,
  TrendingUp,
  Calendar,
  DollarSign,
  Clock,
  FileText,
  Download,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { formatDate } from "@/lib/date-format";

interface RevenueSchedule {
  id: string;
  transactionId: string;
  userId: string;
  userEmail: string;
  organizationId: string | null;
  totalAmount: string;
  recognizedAmount: string;
  deferredAmount: string;
  currency: string;
  recognitionMethod: string;
  serviceStartDate: string;
  serviceEndDate: string;
  totalDays: number;
  status: string;
  lastRecognizedAt: string | null;
  createdAt: string;
}

interface RecognitionEntry {
  id: string;
  revenueRecognitionId: string;
  recognitionDate: string;
  periodStart: string;
  periodEnd: string;
  amount: string;
  currency: string;
  isManualAdjustment: boolean;
  adjustmentReason: string | null;
  createdAt: string;
}

interface MonthlyReport {
  period: string;
  recognizedAmount: number;
  deferredAmount: number;
  transactionCount: number;
  currency: string;
}

interface DeferredReport {
  asOfDate: string;
  totalDeferred: number;
  byTransaction: {
    transactionId: string;
    userId: string;
    totalAmount: number;
    recognizedAmount: number;
    deferredAmount: number;
    serviceEndDate: string;
  }[];
}

export default function RevenueRecognitionDashboard() {
  const { toast } = useToast();
  const [selectedPeriod, setSelectedPeriod] = useState("6m");
  const [expandedSchedule, setExpandedSchedule] = useState<string | null>(null);

  const { data: schedules, isLoading: schedulesLoading, refetch: refetchSchedules } = useQuery<RevenueSchedule[]>({
    queryKey: ["/api/admin/revenue-recognition/schedules"],
  });

  const { data: monthlyReport, isLoading: reportLoading } = useQuery<MonthlyReport[]>({
    queryKey: ["/api/admin/revenue-recognition/report", selectedPeriod],
  });

  const { data: deferredReport } = useQuery<DeferredReport>({
    queryKey: ["/api/admin/revenue-recognition/deferred"],
  });

  const { data: entries } = useQuery<RecognitionEntry[]>({
    queryKey: ["/api/admin/revenue-recognition/entries", expandedSchedule],
    enabled: !!expandedSchedule,
  });

  const processNow = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/admin/revenue-recognition/process");
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/revenue-recognition"] });
      toast({
        title: "Processing Complete",
        description: `Processed ${data.processed} schedules, recognized $${data.totalAmount.toFixed(2)}`,
      });
    },
    onError: (error: any) => {
      toast({ title: "Processing Failed", description: error.message, variant: "destructive" });
    },
  });

  const formatCurrency = (amount: number | string, currency: string = "USD") => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(num);
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      active: "default",
      fully_recognized: "secondary",
      cancelled: "destructive",
      refunded: "destructive",
    };
    const labels: Record<string, string> = {
      active: "Active",
      fully_recognized: "Complete",
      cancelled: "Cancelled",
      refunded: "Refunded",
    };
    return <Badge variant={variants[status] || "outline"}>{labels[status] || status}</Badge>;
  };

  const getRecognitionProgress = (schedule: RevenueSchedule) => {
    const total = parseFloat(schedule.totalAmount);
    const recognized = parseFloat(schedule.recognizedAmount);
    return total > 0 ? (recognized / total) * 100 : 0;
  };

  // Calculate totals
  const totals = schedules?.reduce(
    (acc, s) => ({
      total: acc.total + parseFloat(s.totalAmount),
      recognized: acc.recognized + parseFloat(s.recognizedAmount),
      deferred: acc.deferred + parseFloat(s.deferredAmount),
    }),
    { total: 0, recognized: 0, deferred: 0 }
  ) || { total: 0, recognized: 0, deferred: 0 };

  // Chart data
  const chartData = monthlyReport?.map((r) => ({
    period: r.period,
    recognized: r.recognizedAmount,
    deferred: r.deferredAmount,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totals.total)}</div>
            <p className="text-xs text-muted-foreground mt-1">From all subscriptions</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              Recognized Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(totals.recognized)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {totals.total > 0 ? ((totals.recognized / totals.total) * 100).toFixed(1) : 0}% of total
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-500" />
              Deferred Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{formatCurrency(totals.deferred)}</div>
            <p className="text-xs text-muted-foreground mt-1">Future recognition</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Active Schedules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{schedules?.filter((s) => s.status === "active").length || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Of {schedules?.length || 0} total</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">
            <TrendingUp className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="schedules">
            <Calendar className="h-4 w-4 mr-2" />
            Schedules
          </TabsTrigger>
          <TabsTrigger value="deferred">
            <Clock className="h-4 w-4 mr-2" />
            Deferred Revenue
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Revenue Recognition Trend</CardTitle>
                  <CardDescription>Monthly recognized vs deferred revenue (ASC 606 / IFRS 15)</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3m">3 Months</SelectItem>
                      <SelectItem value="6m">6 Months</SelectItem>
                      <SelectItem value="12m">12 Months</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={() => processNow.mutate()}
                    disabled={processNow.isPending}
                  >
                    {processNow.isPending ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Process Now
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {reportLoading ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorRecognized" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorDeferred" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="period" className="text-xs" />
                    <YAxis className="text-xs" tickFormatter={(v) => `$${v / 1000}k`} />
                    <Tooltip
                      formatter={(value: number) => formatCurrency(value)}
                      labelFormatter={(label) => `Period: ${label}`}
                    />
                    <Area
                      type="monotone"
                      dataKey="recognized"
                      stroke="#10b981"
                      fillOpacity={1}
                      fill="url(#colorRecognized)"
                      name="Recognized"
                    />
                    <Area
                      type="monotone"
                      dataKey="deferred"
                      stroke="#f59e0b"
                      fillOpacity={1}
                      fill="url(#colorDeferred)"
                      name="Deferred"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <TrendingUp className="h-12 w-12 mb-4 opacity-50" />
                  <p>No revenue recognition data available</p>
                  <p className="text-sm">Revenue will be recognized as subscriptions are processed</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="schedules" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recognition Schedules</CardTitle>
                  <CardDescription>All revenue recognition schedules with progress tracking</CardDescription>
                </div>
                <Button variant="outline" onClick={() => refetchSchedules()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {schedulesLoading ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[30px]"></TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Recognized</TableHead>
                        <TableHead>Deferred</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead>Service Period</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!schedules || schedules.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                            No revenue recognition schedules found.
                          </TableCell>
                        </TableRow>
                      ) : (
                        schedules.map((schedule) => (
                          <>
                            <TableRow
                              key={schedule.id}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() =>
                                setExpandedSchedule(expandedSchedule === schedule.id ? null : schedule.id)
                              }
                            >
                              <TableCell>
                                {expandedSchedule === schedule.id ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="font-medium">{schedule.userEmail}</div>
                                <div className="text-xs text-muted-foreground">
                                  {schedule.transactionId.slice(0, 12)}...
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="capitalize">
                                  {schedule.recognitionMethod.replace("_", " ")}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono">
                                {formatCurrency(schedule.totalAmount, schedule.currency)}
                              </TableCell>
                              <TableCell className="font-mono text-green-600">
                                {formatCurrency(schedule.recognizedAmount, schedule.currency)}
                              </TableCell>
                              <TableCell className="font-mono text-amber-600">
                                {formatCurrency(schedule.deferredAmount, schedule.currency)}
                              </TableCell>
                              <TableCell>
                                <div className="w-24">
                                  <Progress value={getRecognitionProgress(schedule)} className="h-2" />
                                  <span className="text-xs text-muted-foreground">
                                    {getRecognitionProgress(schedule).toFixed(0)}%
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell className="text-sm">
                                <div>{formatDate(schedule.serviceStartDate, "MMM d, yyyy")}</div>
                                <div className="text-muted-foreground">
                                  to {formatDate(schedule.serviceEndDate, "MMM d, yyyy")}
                                </div>
                              </TableCell>
                              <TableCell>{getStatusBadge(schedule.status)}</TableCell>
                            </TableRow>
                            {expandedSchedule === schedule.id && entries && (
                              <TableRow>
                                <TableCell colSpan={9} className="bg-muted/30 p-4">
                                  <div className="text-sm font-medium mb-2">Recognition Entries</div>
                                  <div className="border rounded-lg bg-background">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>Date</TableHead>
                                          <TableHead>Period</TableHead>
                                          <TableHead>Amount</TableHead>
                                          <TableHead>Type</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {entries
                                          .filter((e) => e.revenueRecognitionId === schedule.id)
                                          .map((entry) => (
                                            <TableRow key={entry.id}>
                                              <TableCell>
                                                {formatDate(entry.recognitionDate, "MMM d, yyyy")}
                                              </TableCell>
                                              <TableCell className="text-muted-foreground">
                                                {entry.periodStart} - {entry.periodEnd}
                                              </TableCell>
                                              <TableCell className="font-mono">
                                                {formatCurrency(entry.amount, entry.currency)}
                                              </TableCell>
                                              <TableCell>
                                                {entry.isManualAdjustment ? (
                                                  <Badge variant="outline">Adjustment</Badge>
                                                ) : (
                                                  <Badge variant="secondary">Auto</Badge>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="deferred" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Deferred Revenue Report</CardTitle>
                  <CardDescription>
                    Revenue that has been collected but not yet recognized (liability on balance sheet)
                  </CardDescription>
                </div>
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export Report
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {deferredReport ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg">
                    <div>
                      <div className="text-sm text-muted-foreground">Total Deferred Revenue</div>
                      <div className="text-3xl font-bold text-amber-600">
                        {formatCurrency(deferredReport.totalDeferred)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-muted-foreground">As of</div>
                      <div className="font-medium">
                        {formatDate(deferredReport.asOfDate, "MMMM d, yyyy")}
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Transaction</TableHead>
                          <TableHead>User</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Recognized</TableHead>
                          <TableHead className="text-right">Deferred</TableHead>
                          <TableHead>Service Ends</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deferredReport.byTransaction.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                              No deferred revenue at this time.
                            </TableCell>
                          </TableRow>
                        ) : (
                          deferredReport.byTransaction.map((item) => (
                            <TableRow key={item.transactionId}>
                              <TableCell className="font-mono text-sm">
                                {item.transactionId.slice(0, 12)}...
                              </TableCell>
                              <TableCell>{item.userId.slice(0, 8)}...</TableCell>
                              <TableCell className="text-right font-mono">
                                {formatCurrency(item.totalAmount)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-green-600">
                                {formatCurrency(item.recognizedAmount)}
                              </TableCell>
                              <TableCell className="text-right font-mono text-amber-600">
                                {formatCurrency(item.deferredAmount)}
                              </TableCell>
                              <TableCell>
                                {formatDate(item.serviceEndDate, "MMM d, yyyy")}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
